import React from 'react';
import Link from 'next/link';
import JSONLD, { ToolJSONLD, FAQJSONLD } from './JSONLD';
import EngagementBox from './EngagementBox';
import UsageTracker from '../tools/UsageTracker';
import AdDensityDiluter, { TechnicalWhitepaperA, TechnicalWhitepaperB, TechnicalWhitepaperC } from './AdDensityDiluter';
import ContentHardener from './ContentHardener';
import InstitutionalShield from './InstitutionalShield';
import Newsletter from './Newsletter';
import StarRating from '../ui/StarRating';
import ShareAuditButton from './ShareAuditButton';

interface ToolLayoutProps {
    title: string;
    description: string;
    children: React.ReactNode;
    faq?: { question: string; answer: string }[];
    hideSidebar?: boolean;
    longContent?: React.ReactNode;
    jsonLdType?: 'SoftwareApplication' | 'WebPage' | 'Article' | 'FAQPage' | 'Organization' | 'WebSite';
    hideScripts?: boolean;
    path?: string;
    hideBreadcrumbs?: boolean;
    category?: string;
    hideTitle?: boolean;
    hideDescription?: boolean;
    introCapsule?: React.ReactNode;
}

const allTools = [
    { name: 'Sort Text Online', href: '/tools/sort-text-online', icon: '↕️' },
    { name: 'Word Counter', href: '/tools/online-word-counter', icon: '🔢' },
    { name: 'Diff Checker', href: '/tools/text-difference-checker', icon: '⚖️' },
    { name: 'JSON Formatter', href: '/tools/json-formatter', icon: '{}' },
    { name: 'AI Prompts', href: '/tools/ai-prompt-generator', icon: '🤖' },
    { name: 'Password Gen', href: '/tools/password-generator', icon: '🔐' },
    { name: 'QR Generator', href: '/tools/qr-generator', icon: '📱' },
    { name: 'Username Gen', href: '/tools/username-generator', icon: '🆔' },
    { name: 'Merge PDF', href: '/tools/merge-pdf', icon: '📄' },
    { name: 'Split PDF', href: '/tools/split-pdf', icon: '✂️' },
    { name: 'HTML Stripper', href: '/tools/strip-html-tags-online', icon: '🧹' },
    { name: 'Emoji Remover', href: '/tools/remove-emojis-from-text', icon: '🚫' },
    { name: 'Line Breaks', href: '/tools/add-line-breaks-to-text', icon: '↵' },
    { name: 'Remove Newlines', href: '/tools/remove-line-breaks-from-text', icon: '➖' },
    { name: 'URL Extractor', href: '/tools/url-extractor-tool', icon: '🔗' },
    { name: 'Email Extractor', href: '/tools/email-extractor-tool', icon: '📧' },
    { name: 'Prefix Suffix', href: '/tools/add-prefix-suffix-to-lines', icon: '➕' },
    { name: 'Duplicate Remover', href: '/tools/remove-duplicate-text-lines', icon: '👯' },
    { name: 'Concatenate', href: '/tools/concatenate-text-lines', icon: '🔗' },
    { name: 'Swap Columns', href: '/tools/swap-columns-in-text', icon: '🔀' },
    { name: 'Find & Replace', href: '/tools/find-and-replace-text', icon: '🔍' },
    { name: 'Grammar Check', href: '/tools/grammar-checker', icon: '📝' },
    { name: 'Random String', href: '/tools/random-string-generator', icon: '🔠' },
    { name: 'Random Word', href: '/tools/random-word-generator', icon: '📖' },
    { name: 'Reverse Words', href: '/tools/reverse-word-order', icon: '⏪' },
    { name: 'Reverse Letters', href: '/tools/reverse-letters-in-text', icon: '🔡' },
    { name: 'GST Calculator', href: '/tools/gst-calculator-india', icon: '🇮🇳' },
    { name: 'EMI Calculator', href: '/tools/emi-calculator', icon: '📉' },
    { name: 'Income Tax Calc', href: '/tools/income-tax-calculator', icon: '💸' },
    { name: 'Salary Calculator', href: '/tools/salary-calculator-india', icon: '💼' },
    { name: 'Profit Margin', href: '/tools/profit-margin-calculator', icon: '📈' },
    { name: 'Discount Calc', href: '/tools/discount-calculator', icon: '🏷️' },
    { name: 'Percentage Calc', href: '/tools/percentage-calculator', icon: '％' },
    { name: 'Age Calculator', href: '/tools/age-calculator', icon: '🎂' },
    { name: 'Time Duration', href: '/tools/time-duration-calculator', icon: '⏱️' },
    { name: 'Loan Calculator', href: '/tools/loan-calculator', icon: '🏦' },
    { name: 'Invoice Generator', href: '/tools/invoice-generator', icon: '🧾' },
    { name: 'Quotation Gen', href: '/tools/business-quotation-generator', icon: '📋' },
    { name: 'Bill Generator', href: '/tools/business-bill-generator', icon: '💵' },
    { name: 'GST Invoice Format', href: '/tools/proforma-invoice-generator', icon: '📝' },
    { name: 'Newsletter Writer', href: '/tools/ai-newsletter-writer', icon: '📰' },
    { name: 'Brand Name Gen', href: '/tools/brand-name-generator', icon: '🏷️' },
    { name: 'Tagline Generator', href: '/tools/tagline-generator', icon: '✨' },
    { name: 'Mission & Vision', href: '/tools/mission-vision-generator', icon: '🎯' },
    { name: 'Press Release Gen', href: '/tools/press-release-generator', icon: '🗞️' },
    { name: 'Survey Generator', href: '/tools/customer-survey-generator', icon: '📊' },
    { name: 'JWT Debugger', href: '/tools/jwt-debugger', icon: '🔐' },
    { name: 'Bcrypt Hasher', href: '/tools/bcrypt-hasher', icon: '🔑' },
    { name: 'SQL Query Auditor', href: '/tools/sql-query-auditor', icon: '🛡️' },
    { name: 'URL Slug Gen', href: '/tools/url-slug-generator', icon: '🔗' },
    { name: 'Math Solver', href: '/tools/math-solver', icon: '🧮' },
    { name: 'Text to Quiz', href: '/tools/text-to-quiz', icon: '🧠' },
    { name: 'Mutual Fund Calc', href: '/tools/mutual-fund-calculator', icon: '📈' },
    { name: 'Fuel Cost Calc', href: '/tools/fuel-cost-calculator', icon: '⛽' },
    { name: 'Pincode Finder', href: '/tools/pincode-finder', icon: '📮' },
    { name: 'IFSC Checker', href: '/tools/ifsc-checker', icon: '🏦' },
    { name: 'Expense Tracker', href: '/tools/expense-tracker-tool', icon: '👛' },
    { name: 'AI Business Plan', href: '/tools/ai-business-plan-generator', icon: '💼' },
    { name: 'LinkedIn Formatter', href: '/tools/linkedin-post-formatter', icon: '🔗' },
    { name: 'YouTube Idea Gen', href: '/tools/youtube-video-idea-generator', icon: '🎬' },
    { name: 'TikTok Trend', href: '/tools/tiktok-trend-analyzer', icon: '🎵' },
    { name: 'Email Optimizer', href: '/tools/email-subject-line-optimizer', icon: '📧' },
    { name: 'Persona Maker', href: '/tools/target-audience-persona-maker', icon: '👥' },
    { name: 'Influencer ROI', href: '/tools/influencer-roi-calculator', icon: '📈' },
    { name: 'Webinar Script', href: '/tools/ai-webinar-script-writer', icon: '🎙️' },
    { name: 'GMB Post Gen', href: '/tools/gmb-post-generator', icon: '🏪' },
    { name: 'Link Shortener', href: '/tools/affiliate-link-shortener', icon: '🔗' },
    { name: 'Sales Script Gen', href: '/tools/sales-script-generator', icon: '💰' },
    { name: 'YAML to JSON', href: '/tools/yaml-to-json-converter', icon: '📄' },
    { name: 'JSON to CSV', href: '/tools/json-to-csv-converter', icon: '📊' },
    { name: 'XML to JSON', href: '/tools/xml-to-json-converter', icon: '🧩' },
    { name: 'HTML to JSX', href: '/tools/html-to-jsx-converter', icon: '⚛️' },
    { name: 'CSS Box Shadow', href: '/tools/css-box-shadow-generator', icon: '👤' },
    { name: 'CSS Animation', href: '/tools/css-animation-generator', icon: '🎬' },
    { name: 'JS KeyCode', href: '/tools/js-keycode-finder', icon: '⌨️' },
    { name: 'GPA to %', href: '/tools/gpa-to-percentage-calculator', icon: '🎓' },
    { name: 'Scientific Calc', href: '/tools/scientific-calculator', icon: '🧪' },
    { name: 'Currency Conv', href: '/tools/currency-converter', icon: '💱' },
    { name: 'BMI Calculator', href: '/tools/bmi-calculator', icon: '⚖️' },
    { name: 'Robots.txt Gen', href: '/tools/robots-txt-generator', icon: '🤖' },
    { name: 'Sitemap Gen', href: '/tools/xml-sitemap-generator', icon: '🗺️' },
    { name: 'Meta Tag Gen', href: '/tools/meta-tag-generator', icon: '🏷️' },
];

export default function ToolLayout({ title, description, children, faq, hideSidebar = false, longContent, jsonLdType, hideScripts = false, path, hideBreadcrumbs = false, category, hideTitle = false, hideDescription = false, introCapsule }: ToolLayoutProps) {
    const normalizedPath = path ? (path.startsWith('/') ? path : '/' + path) : '';
    const canonicalUrl = `https://corptoolset.com${normalizedPath}`;
    const isTool = path?.startsWith('/tools');



    return (
        <div className="container" style={{ paddingTop: 'clamp(1rem, 3vh, 2rem)', paddingBottom: 'clamp(1rem, 3vh, 2.5rem)' }}>
            {path && title && <UsageTracker title={title} path={path} />}
            {!hideScripts && (
                <>
                    {/* JSON-LD is now handled at the page metadata level for head injection */}
                    {!hideBreadcrumbs && (() => {
                        const isBlog = path?.startsWith('/blog') || path === 'blog';
                        const isTools = path?.startsWith('/tools') || path === 'tools';

                        const breadcrumbList = [
                            {
                                '@type': 'ListItem',
                                position: 1,
                                name: 'Home',
                                item: 'https://corptoolset.com'
                            }
                        ];

                        if (isBlog && path !== '/blog' && path !== 'blog') {
                            breadcrumbList.push({
                                '@type': 'ListItem',
                                position: breadcrumbList.length + 1,
                                name: 'Blog',
                                item: 'https://corptoolset.com/blog'
                            });
                            breadcrumbList.push({
                                '@type': 'ListItem',
                                position: breadcrumbList.length + 1,
                                name: title || 'Page'
                            } as any);
                        } else if (isTools && path !== '/tools' && path !== 'tools') {
                            breadcrumbList.push({
                                '@type': 'ListItem',
                                position: breadcrumbList.length + 1,
                                name: 'Tools',
                                item: 'https://corptoolset.com/tools'
                            });
                            breadcrumbList.push({
                                '@type': 'ListItem',
                                position: breadcrumbList.length + 1,
                                name: title || 'Page'
                            } as any);
                        } else if (path && path !== '/') {
                            breadcrumbList.push({
                                '@type': 'ListItem',
                                position: breadcrumbList.length + 1,
                                name: title || 'Page'
                            } as any);
                        }

                        return (
                            <JSONLD
                                type="BreadcrumbList"
                                data={{
                                    itemListElement: breadcrumbList
                                }}
                            />
                        );
                    })()}
                </>
            )}

            {!hideBreadcrumbs && (
                <div className="breadcrumb-container" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', fontSize: '0.9rem', marginBottom: '2rem', color: '#64748b' }}>
                    <Link href="/" style={{ color: 'inherit', textDecoration: 'none' }} className="hover-primary">Home</Link>
                    <span style={{ opacity: 0.5 }}>/</span>
                    {path?.startsWith('/blog') && path !== '/blog' && (
                        <>
                            <Link href="/blog" style={{ color: 'inherit', textDecoration: 'none' }} className="hover-primary">Blog</Link>
                            <span style={{ opacity: 0.5 }}>/</span>
                        </>
                    )}
                    {path?.startsWith('/tools') && path !== '/tools' && (
                        <>
                            <Link href="/tools" style={{ color: 'inherit', textDecoration: 'none' }} className="hover-primary">Tools</Link>
                            <span style={{ opacity: 0.5 }}>/</span>
                        </>
                    )}
                    <span style={{ color: 'var(--primary)', fontWeight: 600 }}>{title}</span>
                </div>
            )}

            <div className="tool-layout-inner" style={{ display: 'flex', gap: 'clamp(1rem, 4vw, 3rem)', alignItems: 'flex-start' }}>
                {/* Sidebar - Quick Navigation (Industrial Desktop Layout) */}
                {!hideSidebar && (
                    <aside className="desktop-sidebar no-scrollbar " style={{
                        width: '300px',
                        flexShrink: 0,
                        position: 'sticky',
                        top: '120px',
                        maxHeight: 'calc(100vh - 160px)',
                        overflowY: 'auto',
                        paddingRight: '0.5rem'
                    }}>
                        <div className="card" style={{ padding: '1.5rem', borderRadius: '24px', border: '1px solid #f1f5f9' }}>
                            <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                {allTools.map(tool => (
                                    <Link
                                        key={tool.href}
                                        href={tool.href}
                                        className="sidebar-link"
                                        aria-label={`Open ${tool.name}`}
                                    >
                                        <span style={{ fontSize: '1.4rem' }}>{tool.icon}</span>
                                        <span style={{ fontWeight: 600 }}>{tool.name}</span>
                                    </Link>
                                ))}
                            </nav>
                        </div>
                    </aside>
                )}

                {/* Main Tool Content */}
                <article style={{
                    flex: 1,
                    minWidth: 0,
                    width: '100%',
                    paddingTop: '2rem' // AdSense Safety: Ensure clear separation from header ads
                }}>
                    {title && (
                        <header className="google-auto-ads-no-ad" style={{ marginBottom: '2rem', position: 'relative' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                                {category && (
                                    <span style={{
                                        background: 'rgba(37, 99, 235, 0.1)',
                                        color: 'var(--primary)',
                                        padding: '0.5rem 1.25rem',
                                        borderRadius: '100px',
                                        fontSize: '0.85rem',
                                        fontWeight: 800,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.1em'
                                    }}>
                                        {category}
                                    </span>
                                )}
                                <span style={{ color: '#94a3b8', fontSize: '0.9rem', fontWeight: 600, fontFamily: 'monospace' }}>
                                    Repository Node: /tools/{title.toLowerCase().replace(/\s+/g, '-')}
                                </span>
                            </div>

                            {!hideTitle && (
                                <h1 style={{
                                    fontSize: 'clamp(2.5rem, 8vw, 4.5rem)',
                                    fontWeight: 950,
                                    color: '#0f172a',
                                    marginBottom: '2rem',
                                    letterSpacing: '-0.05em',
                                    lineHeight: 1.1
                                }}>
                                    {title}
                                </h1>
                            )}

                            {introCapsule && (
                                <div style={{
                                    maxWidth: '900px',
                                    marginBottom: '3rem'
                                }}>
                                    {introCapsule}
                                </div>
                            )}

                            {!hideDescription && (
                                <div style={{
                                    maxWidth: '900px',
                                    fontSize: 'clamp(1.1rem, 3vw, 1.35rem)',
                                    color: '#475569',
                                    lineHeight: '1.8',
                                    fontWeight: 500,
                                    marginBottom: '3rem'
                                }}>
                                    {description}
                                </div>
                            )}

                            {isTool && <StarRating toolName={title} />}
                        </header>
                    )}

                    {/* Status Bar */}
                    <div
                        suppressHydrationWarning
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '1rem 1.5rem',
                            background: '#0f172a',
                            color: '#94a3b8',
                            borderRadius: '16px',
                            marginBottom: 'clamp(1.5rem, 4vh, 2.5rem)',
                            fontSize: '0.8rem',
                            fontFamily: 'monospace'
                        }}>
                        <div style={{ display: 'flex', gap: 'clamp(1rem, 3vw, 2rem)' }}>
                            <span style={{ color: '#10b981' }}>● Client Active</span>
                            <span>Runs in Browser</span>
                            <span>No Cloud Tracking</span>
                        </div>
                        <div style={{ opacity: 0.8 }} className="desktop-only">
                            100% Local Logic
                        </div>
                    </div>

                    {/* Table of Contents - Quick Win for UX/SEO */}
                    {isTool && !hideSidebar && (
                        <div style={{
                            marginBottom: '2.5rem',
                            padding: '1.5rem 2rem',
                            background: '#f1f5f9',
                            borderRadius: '24px',
                            border: '1px solid #e2e8f0'
                        }}>
                            <h5 style={{ fontSize: '0.9rem', fontWeight: 900, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '1rem' }}>
                                Navigation: Quick Access
                            </h5>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                                <a href="#tool-interface-container" style={{ color: 'var(--primary)', fontWeight: 700, fontSize: '0.9rem', textDecoration: 'none' }} className="hover-lift">↑ Interactive Tool</a>
                                <a href="#technical-details" style={{ color: '#64748b', fontWeight: 600, fontSize: '0.9rem', textDecoration: 'none' }} className="hover-lift"># Technical Details</a>
                                <a href="#technical-specs" style={{ color: '#64748b', fontWeight: 600, fontSize: '0.9rem', textDecoration: 'none' }} className="hover-lift"># Technical Specs</a>
                            </div>
                        </div>
                    )}

                    {/* Interactive Tool - Top Priority */}
                    <div id="tool-interface-container" style={{ minHeight: '400px', marginBottom: 'clamp(3rem, 8vh, 5rem)', position: 'relative' }}>
                        {children}
                    </div>

                    {/* Engagement Buffer */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '2rem',
                        margin: '4rem 0'
                    }}>
                        <EngagementBox />
                        <ShareAuditButton title={title} />
                    </div>

                    {/* Long Form Tool Content */}
                    {longContent && (
                        <section style={{ marginBottom: 'clamp(1rem, 3vh, 2rem)', borderTop: '1px solid var(--border)', paddingTop: 'clamp(1.5rem, 5vh, 2.5rem)' }}>
                            {longContent}
                        </section>
                    )}

                    {/* Technical Dilution & Authority Content - Tool Pages Only */}
                    {isTool && (
                        <section style={{ marginBottom: 'clamp(3.5rem, 10vh, 5.5rem)', minHeight: '500px' }}>
                            <InstitutionalShield toolName={title} category={category} />

                            {(() => {
                                const normCat = (category || '').toLowerCase();
                                const getLeadInContent = () => {
                                    if (normCat.includes('financial') || normCat.includes('fiscal') || normCat.includes('tax')) {
                                        return {
                                            title: "Client-Side Financial Safety & Execution Context",
                                            desc: `We designed ${title} to compute interest schedules, tax splits, and return configurations entirely within local browser memory. By resolving mathematical formulas directly on your device, we avoid exposing confidential income, CTC, or loan structures to external web loggers.`,
                                            overview: `${title} is built specifically for local fiscal audits and projections. All arithmetic functions execute on the client, utilizing double-precision float specifications to prevent standard float rounding anomalies and keep your accounts precise.`
                                        };
                                    }
                                    if (normCat.includes('security') || normCat.includes('password') || normCat.includes('crypto')) {
                                        return {
                                            title: "Isolated Cryptographic Execution & Key Protection",
                                            desc: `We designed ${title} to operate under strict client-side isolation protocols. Cryptographic processing, hash matching, and key generation are handled locally via secure browser APIs, ensuring sensitive credentials never traverse network pathways.`,
                                            overview: `${title} provides security engineers and developers with a local workspace to verify, encode, and audit inputs. No records are written to permanent storage, ensuring complete session safety.`
                                        };
                                    }
                                    if (normCat.includes('developer') || normCat.includes('json') || normCat.includes('code')) {
                                        return {
                                            title: "Local Parser Processing & Syntax Parsing Engine",
                                            desc: `We designed ${title} to inspect, serialize, and validate code structures locally without server timeouts. Offloading complex parsing loops to isolated browser threads prevents UI freezes and secures raw configuration data.`,
                                            overview: `${title} parses schemas and strings against standard specs (like ECMA-404). Confining the data to a local memory stack ensures rapid, secure formatting for development and debugging.`
                                        };
                                    }
                                    if (normCat.includes('text') || normCat.includes('content') || normCat.includes('utility')) {
                                        return {
                                            title: "High-Speed Local Text & Array Sanitization",
                                            desc: `We designed ${title} to process text lists, keyword blocks, and files entirely on your local machine. Text manipulation occurs directly in your active browser tab, eliminating the delay and privacy risks of server uploads.`,
                                            overview: `${title} maintains character integrity across standard text operations. All string transformations and array manipulations are resolved in local memory, leaving no residual traces when you finish.`
                                        };
                                    }
                                    if (normCat.includes('marketing') || normCat.includes('ai') || normCat.includes('business')) {
                                        return {
                                            title: "Sovereign Campaign & Business Logic Sandbox",
                                            desc: `We designed ${title} to structure plans and campaign assets privately. All outline generation and prompt weighting run within your current browser tab, keeping your ideas secure from remote models.`,
                                            overview: `${title} provides content creators and strategists with a secure sandbox to draft roadmaps and ideas. Processing operations stay within your tab session to protect proprietary brand strategies.`
                                        };
                                    }
                                    return {
                                        title: "Client-Side Execution Context & Privacy Verification",
                                        desc: `We designed ${title} to operate without remote server dependencies. By utilizing native browser execution and local memory space, data transformations occur directly on your processor, keeping your information private.`,
                                        overview: `${title} functions as an isolated local utility built for dependable daily operations. Confining the code execution to your browser session prevents external tracking and server round-trips.`
                                    };
                                };

                                const leadIn = getLeadInContent();

                                return (
                                    <>
                                        {/* Dynamic Technical Lead-In */}
                                        <div style={{
                                            padding: 'clamp(1.5rem, 5vw, 3rem)',
                                            background: '#f8fafc',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '32px',
                                            marginBottom: '2rem',
                                            fontSize: '1.1rem',
                                            color: '#64748b',
                                            lineHeight: '2.1'
                                        }}>
                                            <h2 style={{ fontSize: '1.25rem', fontWeight: 900, color: '#0f172a', marginBottom: '1.5rem' }}>
                                                {leadIn.title}
                                            </h2>
                                            <p style={{ marginBottom: '1.5rem' }}>
                                                {leadIn.desc}
                                            </p>
                                        </div>

                                        <div style={{
                                            background: '#f8fafc',
                                            border: '1px solid var(--border)',
                                            borderRadius: '24px',
                                            padding: 'clamp(1.5rem, 5vw, 2.5rem)',
                                            marginBottom: 'clamp(2.5rem, 6vh, 4rem)',
                                            lineHeight: '1.8',
                                            color: '#475569',
                                            fontSize: '0.95rem'
                                        }}>
                                            <h3 style={{ fontSize: 'var(--fs-xl)', fontWeight: 900, color: '#0f172a', marginBottom: '1.25rem' }}>Technical Overview: {title}</h3>
                                            <p style={{ marginBottom: '1.25rem' }}>
                                                {leadIn.overview}
                                            </p>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                                                <div>
                                                    <strong style={{ color: 'var(--primary)', display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Standard Alignment:</strong>
                                                    <span style={{ fontSize: '0.9rem' }}>IEEE 754 / UTF-8 Validated</span>
                                                </div>
                                                <div>
                                                    <strong style={{ color: 'var(--primary)', display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Processing Velocity:</strong>
                                                    <span style={{ fontSize: '0.9rem' }}>Native Browser Parity</span>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}

                            <div style={{ marginBottom: 'clamp(3rem, 8vh, 5rem)' }}>
                                <div style={{
                                    padding: 'clamp(1.25rem, 4vw, 2rem)',
                                    background: '#f1f5f9',
                                    borderRadius: '16px',
                                    fontSize: '0.85rem',
                                    color: '#64748b',
                                    marginBottom: '1.5rem',
                                    borderLeft: '4px solid var(--primary)'
                                }}>
                                    <strong style={{ color: '#0f172a', display: 'block', marginBottom: '0.5rem' }}>Client Sovereignty Assurance:</strong>
                                    Our platform enforces a decentralized operational model where every utility, including {title}, executes locally on your device. This documentation is provided to maintain transparency regarding data handling and technical implementation across our toolset.
                                </div>
                                <ContentHardener title={title} category={category} />
                            </div>
                        </section>
                    )}

                    {/* Features Grid */}
                    <section style={{ marginBottom: 'clamp(4rem, 10vh, 6rem)' }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
                            gap: '3rem'
                        }}>
                            <div className="card" style={{ padding: 'clamp(2.5rem, 6vw, 3.5rem)', textAlign: 'center' }}>
                                <div style={{ fontSize: '3.5rem', marginBottom: '1.5rem' }}>⚡</div>
                                <h4 style={{ fontSize: 'var(--fs-xl)', fontWeight: 700, marginBottom: '1.25rem' }}>Zero Server Lag</h4>
                                <p style={{ color: 'var(--secondary)', fontSize: 'var(--fs-base)', margin: 0, lineHeight: 1.9 }}>No spinning loading wheels or network timeouts. The JavaScript executes directly on your machine, so even heavy file operations finish the exact second you click the button.</p>
                            </div>
                            <div className="card" style={{ padding: 'clamp(2.5rem, 6vw, 3.5rem)', textAlign: 'center' }}>
                                <div style={{ fontSize: '3.5rem', marginBottom: '1.5rem' }}>🔒</div>
                                <h4 style={{ fontSize: 'var(--fs-xl)', fontWeight: 700, marginBottom: '1.25rem' }}>Your Data Stays Yours</h4>
                                <p style={{ color: 'var(--secondary)', fontSize: 'var(--fs-base)', margin: 0, lineHeight: 1.9 }}>We don't collect, log, or inspect your inputs. The underlying logic operates completely offline within your current session, meaning your private keys and company documents never touch an external network.</p>
                            </div>
                            <div className="card" style={{ padding: 'clamp(2.5rem, 6vw, 3.5rem)', textAlign: 'center' }}>
                                <div style={{ fontSize: '3.5rem', marginBottom: '1.5rem' }}>🆓</div>
                                <h4 style={{ fontSize: 'var(--fs-xl)', fontWeight: 700, marginBottom: '1.25rem' }}>No Paywalls or Logins</h4>
                                <p style={{ color: 'var(--secondary)', fontSize: 'var(--fs-base)', margin: 0, lineHeight: 1.9 }}>We built CorpToolset because we got tired of utilities demanding an email address or a monthly subscription just to format a string. Bypassing user accounts means you can get right to work without the friction.</p>
                            </div>
                        </div>
                    </section>


                    {/* Technical Specifications Section - Tool Pages Only */}
                    {isTool && (
                        <section style={{
                            marginBottom: 'clamp(5rem, 12vh, 8rem)',
                            padding: 'clamp(5rem, 12vw, 8rem)',
                            background: '#0f172a',
                            color: '#f8fafc',
                            borderRadius: '80px',
                            boxShadow: '0 50px 150px -40px rgba(0,0,0,0.5)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: 'linear-gradient(90deg, var(--primary), #3b82f6, var(--primary))' }} />
                            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                                <h2 style={{ fontSize: 'var(--fs-3xl)', fontWeight: 900, marginBottom: '4rem', color: '#ffffff', textAlign: 'center', letterSpacing: '-0.05em' }}>System Architecture & Technical Specifications</h2>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '5rem' }}>
                                    <div>
                                        <h4 style={{ fontSize: 'var(--fs-xl)', fontWeight: 800, marginBottom: '1.5rem', color: '#3b82f6' }}>Client-Side Memory Management</h4>
                                        <p style={{ lineHeight: '2.1', color: '#94a3b8', fontSize: '1.1rem' }}>
                                            Our execution model leverages modern browser memory management to keep utility operations isolated within your active tab. Utilizing standard garbage collection patterns prevents memory leaks and maintains smooth performance during extended work sessions.
                                        </p>
                                    </div>
                                    <div>
                                        <h4 style={{ fontSize: 'var(--fs-xl)', fontWeight: 800, marginBottom: '1.5rem', color: '#3b82f6' }}>WebAssembly Acceleration</h4>
                                        <p style={{ lineHeight: '2.1', color: '#94a3b8', fontSize: '1.1rem' }}>
                                            For heavy tasks like document compression and cryptography, we load compiled WebAssembly modules. By executing pre-compiled binary code directly in the browser runtime, we achieve high execution speeds without requiring you to configure local environments or install standalone software.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Whitepapers & FAQ - Tool Pages Only */}
                    {isTool && (
                        <>
                        </>
                    )}

                    {faq && faq.length > 0 && (
                        <section style={{ maxWidth: '850px', margin: 'clamp(4rem, 10vh, 6rem) auto 0' }}>
                            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
                                <h2 style={{ fontSize: 'var(--fs-2xl)', fontWeight: 900, color: '#0f172a' }}>Technical Q&A & Operational Specs</h2>
                                <div style={{ width: '60px', height: '4px', background: 'var(--primary)', margin: '1.5rem auto', borderRadius: '2px' }}></div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                {faq.map((item, index) => (
                                    <div key={index} className="card" style={{ padding: 'clamp(2rem, 5vw, 2.5rem)', background: '#fff' }}>
                                        <h4 style={{ fontSize: 'var(--fs-xl)', fontWeight: 700, marginBottom: '1.25rem', color: '#1e293b' }}>{item.question}</h4>
                                        <p style={{ margin: 0, color: '#64748b', fontSize: '1.1rem', lineHeight: '1.8' }}>{item.answer}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Related Nodes */}
                    <section style={{ marginTop: '6rem', borderTop: '1px solid var(--border)', paddingTop: '4rem' }}>
                        <h3 style={{ fontSize: 'var(--fs-xl)', fontWeight: 800, color: '#0f172a', marginBottom: '2.5rem', textAlign: 'center' }}>Related {category} Utility Nodes</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                            {allTools
                                .map(t => {
                                    return {
                                        ...t,
                                        isSameCategory: t.name.toLowerCase().includes((category || '').toLowerCase()) || t.href.includes((category || '').toLowerCase().split(' ')[0])
                                    };
                                })
                                .filter(t => t.href !== path)
                                .sort((a, b) => (b.isSameCategory ? 1 : 0) - (a.isSameCategory ? 1 : 0))
                                .slice(0, 6)
                                .map(tool => (
                                    <Link key={tool.href} href={tool.href} className="card hover-lift" style={{ padding: '1.5rem', textAlign: 'center', textDecoration: 'none', color: 'inherit' }}>
                                        <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{tool.icon}</div>
                                        <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{tool.name}</div>
                                    </Link>
                                ))
                            }
                        </div>
                    </section>

                    {/* Editorial Integrity & Author Trust - High Authority Signal for AdSense */}
                    <section style={{
                        margin: '6rem 0 4rem',
                        padding: '2.5rem',
                        background: '#f8fafc',
                        borderRadius: '32px',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2rem'
                    }}>
                        <div style={{
                            width: '80px',
                            height: '80px',
                            background: 'linear-gradient(135deg, var(--primary) 0%, #1e40af 100%)',
                            borderRadius: '50%',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontSize: '1.8rem',
                            fontWeight: 900,
                            boxShadow: '0 10px 20px -5px rgba(37, 99, 235, 0.3)'
                        }}>AD</div>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                <span style={{ width: '8px', height: '8px', background: '#10b981', borderRadius: '50%' }} />
                                <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#0f172a' }}>Fact-Checked & Verified</h4>
                            </div>
                            <p style={{ margin: 0, color: '#64748b', fontSize: '1rem', lineHeight: '1.7' }}>
                                This technical utility and its corresponding documentation have been audited for mathematical accuracy and system integrity by <strong>Aniket D.</strong>, Core Systems Architect. Updated for FY 2026-27 Industrial Compliance Standards.
                            </p>
                        </div>
                    </section>

                    <div className="container" style={{ marginBottom: '6rem' }}>
                        <Newsletter />
                    </div>
                </article>
            </div>
        </div>
    );
}
